import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';
import Breadcrumbs from '../components/Breadcrumbs';
import BreadcrumbsJsonLd from '../components/BreadcrumbsJsonLd';

export default function ForInstitutes() {
  useSEO({
    title: 'AI Assessment Platform for Institutes | GreenTick',
    description: 'Elevate university placement rates with AI-driven student assessments. GreenTick provides simulated interview environments and automated skill verification to align students with global industry standards.',
    canonicalPath: '/for-institutes',
  });
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <Navbar />
      <BreadcrumbsJsonLd 
        items={[
          { name: 'Home', item: '/' },
          { name: 'For Institutes', item: '/for-institutes' }
        ]}
      />

      <div className="pt-32 pb-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <Breadcrumbs 
              items={[{ name: 'For Institutes', item: '/for-institutes' }]} 
            />

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Equip Students,
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-emerald-400">
                Elevate Futures
              </span>
            </h1>

            <p className="text-xl text-slate-300 mb-12 leading-relaxed">
              Revolutionize campus placements with AI-driven interview training, real-time industry alignment, and comprehensive student assessments.
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="glass p-8 rounded-2xl">
                <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-6 h-6 text-teal-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Campus Placement Excellence</h3>
                <p className="text-slate-400">
                  Green Tick has revolutionized our campus placements. The AI-driven screening process ensures that students get the best possible opportunities.
                </p>
              </div>

              <div className="glass p-8 rounded-2xl">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Detailed Candidate Reports</h3>
                <p className="text-slate-400">
                  Generate comprehensive reports that help match students with the right companies more efficiently and effectively.
                </p>
              </div>

              <div className="glass p-8 rounded-2xl">
                <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-6 h-6 text-teal-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Seamless Integration</h3>
                <p className="text-slate-400">
                  Effortless integration with existing systems and instant feedback provided to students make Green Tick an invaluable tool.
                </p>
              </div>

              <div className="glass p-8 rounded-2xl">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Realistic Interview Experience</h3>
                <p className="text-slate-400">
                  Interactive AI interviews provide students with a realistic interview experience, preparing them well for their future careers.
                </p>
              </div>
            </div>

            <div className="glass p-12 rounded-2xl mb-12">
              <h2 className="text-3xl font-bold text-white mb-8">What's Included</h2>
              <div className="space-y-4">
                {[
                  'AI-Driven Interview Training',
                  'Real-Time Industry Alignment',
                  'Detailed Profile Assessment',
                  'Actionable Performance Reports',
                  'Dedicated Support',
                  'Student Progress Tracking',
                  'Company-Student Matching'
                ].map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-teal-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300 text-lg">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://dashboard.greentick.org.in/auth"
                target="_blank"
                rel="noopener noreferrer"
                className="group px-8 py-4 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl font-semibold hover:shadow-2xl hover:shadow-teal-500/50 transition-all hover:scale-105 flex items-center gap-2 justify-center"
              >
                Get Started
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <Link
                to="/contact"
                className="px-8 py-4 glass text-white rounded-xl font-semibold hover:bg-white/20 transition-all hover:scale-105 flex items-center justify-center"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
